import React, {useCallback, useState} from "react";
import {PopoverArrowBottom, PrimaryButton, PureButton} from "components/Common";
import {useTranslation} from "react-i18next";
import {Popover} from "@mui/material";
import {ShareTarget} from "modules/enums";
import {CopyHandler, IconFacebook, IconTwitter} from "components";
import styled from "@emotion/styled";
import {useGenerateShareMessage, useIsMobile} from "modules/utils/hooks";
import {share} from "modules/utils/share";
import {useSelector} from "react-redux";
import {getActiveContest, getUserStore} from "modules/selectors";
import {useLocation} from "react-router-dom";
import {EventGUID, createFanHubEvent} from "modules/utils/Services/analytics";

const ShareContainer = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 6px;
	padding: 12px;

	button {
		width: 24px;
		height: 24px;
		display: flex;
		justify-content: center;
		align-items: center;
	}
`;

interface IProps {
	place: "results" | "summary";
	shareCallback?: () => void;
}

export const ShareHandler: React.FC<IProps> = ({shareCallback}) => {
	const {t} = useTranslation();
	const location = useLocation();
	const {isMobileShare} = useIsMobile("(max-width: 720px)");
	const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
	const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
	const contest = useSelector(getActiveContest);
	const userId = useSelector(getUserStore).currentUser?.id;
	const popoverOffset = location.pathname.includes("summary") ? 120 : 30;
	const message = useGenerateShareMessage();

	const onShare = useCallback(
		(e: React.SyntheticEvent<HTMLButtonElement>) => {
			const target = e.currentTarget.dataset.target as ShareTarget;
			if (!target || !contest?.id || !userId) {
				return;
			}

			const fanhubTrackEvent = {
				[ShareTarget.Twitter]: EventGUID.PostpickShareViaX,
				[ShareTarget.Facebook]: EventGUID.PostpickShareViaFB,
				[ShareTarget.Native]: EventGUID.PostpickShareViaNativeMobile,
			}[target];

			createFanHubEvent(fanhubTrackEvent);

			void share({
				target,
				contestId: contest.id,
				userId,
				message,
			});
		},
		[contest, userId, message]
	);

	const shareNative = useCallback(() => {
		if (!contest?.id || !userId) {
			return;
		}

		void share({
			target: ShareTarget.Native,
			contestId: contest.id,
			userId: userId,
			message,
		});
	}, [contest, userId, message]);

	const onClose = () => {
		setIsPopoverOpen(false);
		setAnchorEl(null);
	};

	const onBeforeShare = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			if (shareCallback) {
				shareCallback();
			}

			if (isMobileShare) {
				shareNative();
				return;
			}
			setAnchorEl(event.currentTarget);
			setIsPopoverOpen(true);
		},
		[isMobileShare, shareNative, shareCallback]
	);

	if (!userId || !contest?.id) {
		return null;
	}

	return (
		<React.Fragment>
			<PrimaryButton onClick={onBeforeShare}>{t("modal.game_over.share")}</PrimaryButton>
			<Popover
				className="overflow-popover"
				open={isPopoverOpen}
				anchorEl={anchorEl}
				onClose={onClose}
				anchorOrigin={{
					vertical: -50,
					horizontal: popoverOffset,
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}>
				<ShareContainer>
					<PureButton onClick={onShare} data-target={ShareTarget.Twitter}>
						<IconTwitter color="#222222" height={20} />
					</PureButton>
					<PureButton onClick={onShare} data-target={ShareTarget.Facebook}>
						<IconFacebook color="#222222" height={20} />
					</PureButton>
					<CopyHandler
						clickCallback={() => {
							onClose();
							createFanHubEvent(EventGUID.PostpickShareViaLink);
						}}
					/>
					<PopoverArrowBottom />
				</ShareContainer>
			</Popover>
		</React.Fragment>
	);
};
